const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const storageKey = (signature) =>
  `pilcore.stripe-operation:${encodeURIComponent(String(signature || ""))}`;

export const createResilientStripeOperationStorage = ({
  primary = null,
  fallback = new Map(),
} = {}) => {
  const removed = new Set();
  return {
    getItem(key) {
      if (removed.has(key)) return null;
      try {
        const value = primary?.getItem?.(key);
        if (value != null) return value;
      } catch (_error) {
        // Browser privacy/storage failures fall back to memory.
      }
      return fallback.get(key) || null;
    },
    setItem(key, value) {
      removed.delete(key);
      fallback.set(key, value);
      try {
        primary?.setItem?.(key, value);
      } catch (_error) {
        // The in-memory mirror still provides retry safety for this session.
      }
    },
    removeItem(key) {
      removed.add(key);
      fallback.delete(key);
      try {
        primary?.removeItem?.(key);
      } catch (_error) {
        // The tombstone prevents a stale primary value being reused here.
      }
    },
  };
};

export const buildStripeOperationSignature = ({
  userId,
  businessUnitId,
  groupId,
  reservationId = null,
  paymentMethodId = null,
  action,
  intentId = null,
  amountCents = null,
}) =>
  [
    `user=${Number(userId)}`,
    `bu=${Number(businessUnitId)}`,
    `group=${Number(groupId)}`,
    ...(reservationId == null ? [] : [`reservation=${Number(reservationId)}`]),
    ...(paymentMethodId == null ? [] : [`card=${Number(paymentMethodId)}`]),
    `action=${String(action || "").trim().toLowerCase()}`,
    `intent=${intentId == null ? "none" : Number(intentId)}`,
    `amount=${amountCents == null ? "full" : Number(amountCents)}`,
  ].join("|");

export const createStripeOperationIdempotencyKey = (cryptoApi) => {
  const resolvedCrypto =
    cryptoApi || (typeof window !== "undefined" ? window.crypto : null);
  if (typeof resolvedCrypto?.randomUUID === "function") {
    return resolvedCrypto.randomUUID().toLowerCase();
  }
  if (typeof resolvedCrypto?.getRandomValues !== "function") {
    throw new Error("Secure UUID generation is unavailable.");
  }
  const bytes = resolvedCrypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
  return [
    hex.slice(0, 4).join(""),
    hex.slice(4, 6).join(""),
    hex.slice(6, 8).join(""),
    hex.slice(8, 10).join(""),
    hex.slice(10).join(""),
  ].join("-");
};

export const getOrCreateStripeOperationIdempotencyKey = ({
  signature,
  storage,
  cryptoApi,
}) => {
  if (!signature) throw new Error("A Stripe operation signature is required.");
  const key = storageKey(signature);
  const existing = String(storage?.getItem?.(key) || "").trim();
  if (UUID_PATTERN.test(existing)) return existing.toLowerCase();

  const created = createStripeOperationIdempotencyKey(cryptoApi);
  storage?.setItem?.(key, created);
  return created;
};

export const completeStripeOperationIdempotencyKey = ({
  signature,
  storage,
}) => {
  if (!signature) return;
  storage?.removeItem?.(storageKey(signature));
};

export const stripeIdempotencyRequestConfig = (idempotencyKey) => ({
  headers: { "Idempotency-Key": idempotencyKey },
});

export const stripeOperationKeyShouldRotate = (responseData) =>
  responseData?.idempotency_key_disposition === "rotate";

export const stripeOperationSuccessNotice = ({
  responseData,
  refreshed,
  successMessage,
  recoveredMessage,
  refreshFailedMessage,
}) => {
  if (responseData?.idempotency_replayed === true) {
    return `${recoveredMessage}${
      refreshed
        ? ""
        : " The latest payment data could not be refreshed; reopen this panel to reload it."
    }`;
  }
  return refreshed ? successMessage : refreshFailedMessage;
};
